/**
 * Encounters hooks (TanStack Query) — the stay-history / admission-lifecycle
 * layer for the Patient Administration app (FHIR Encounter-aligned).
 *
 * Distinct bounded context from `components/admin/org/useOrg.ts`'s
 * `useAdmitPatient`/`useDischargePatient`: those are the platform-admin
 * Organisation console's SERVER-AUTHORITATIVE org-structure mutations (the
 * clinical-safety carve-out in frontend/CLAUDE.md — ACL/permission changes
 * never go optimistic). The mutations here are ward-level census work
 * (register/admit/place/discharge a patient) — low-stakes operational state,
 * NOT the clinical-document carve-out — so they ARE optimistic-with-rollback,
 * mirroring `useRegisterPatient` in usePatients.ts. Admitting into a fresh
 * unit reuses `orgApi.admitPatient` (same `POST /locations/{id}/patients`
 * wire call as the console) rather than duplicating it; transfer/discharge
 * are new encounter-scoped endpoints with no console equivalent.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n';
import {
  admittedStay,
  dischargeStay,
  listAdmittedStays,
  listStayPlacements,
  listStays,
  transferStay,
  type StayPlacementResponse,
  type StayResponse,
} from '@/api/endpoints/stays';
import { orgApi } from '@/api/endpoints/org';
import { orgKeys } from '@/components/admin/org/useOrg';
import { patientKeys } from '@/hooks/usePatients';
import { ApiError } from '@/api/client';
import { errorToast } from '@/hooks/errorToast';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useConfigStore } from '@/stores/config';
import { randomUUID } from '@/lib/uuid';
import type { StayConfig } from '@/api/types';

export const encounterKeys = {
  all: ['stays'] as const,
  lists: () => [...encounterKeys.all, 'list'] as const,
  list: (patientId: string) => [...encounterKeys.lists(), patientId] as const,
  /** The caller's whole open caseload, across patients. */
  caseload: () => [...encounterKeys.all, 'caseload'] as const,
  admitted: (patientId: string) => [...encounterKeys.all, 'admitted', patientId] as const,
};

/**
 * Every open stay the caller may see — the ward's caseload.
 *
 * One census request rather than a stay list per patient. A screen that lists
 * beds renders on every patient change, and a request per bed there is the
 * N+1 that makes a ward of thirty patients thirty round trips.
 */
export function useCaseload() {
  return useAuthenticatedQuery<StayResponse[]>({
    queryKey: encounterKeys.caseload(),
    queryFn: () => listAdmittedStays(),
    refetchOnWindowFocus: false,
  });
}

/**
 * The patient's admitted stay, or null when there is none.
 *
 * Null is an answer: a discharged patient HAS no admitted stay, and the caller
 * shows that rather than opening a board on a stay it picked on her behalf.
 * `admittedStay` throws on the two shapes that mean the record is broken (two
 * open stays, or an open stay with no unit) instead of choosing between them.
 */
export function useAdmittedStay(patientId: string | null | undefined) {
  return useAuthenticatedQuery<StayResponse | null>({
    queryKey: encounterKeys.admitted(patientId ?? 'none'),
    queryFn: async () => admittedStay(await listStays(patientId as string)),
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  });
}

/**
 * A stay with its movement log folded in.
 *
 * The stays API keeps the two apart: a stay carries its CURRENT `location`
 * directly, and `placements` is its own sub-resource: because most callers
 * want the location and not the history. This view wants both: the census
 * board renders a timeline. The log is fetched per stay and folded here rather
 * than in the component, so the board keeps one query to read.
 */
export type StayWithPlacements = StayResponse & { placements: StayPlacementResponse[] };

/**
 * Config-driven stay vocabulary from the config store (GET /config `stay`).
 * Returns null only while /config is still loading — the block is required, so
 * a loaded config always carries it and a backend that omits it fails the
 * config parse loudly rather than reaching here as an empty picker. Callers
 * still handle the loading state explicitly; never hardcode the
 * class/disposition enums.
 */
export function useStayConfig(): StayConfig | null {
  return useConfigStore((s) => s.config?.stay ?? null);
}

/** A patient's full stay history, newest first, each with its movement log. */
export function useEncounters(patientId: string | undefined) {
  return useAuthenticatedQuery<StayWithPlacements[]>({
    queryKey: encounterKeys.list(patientId ?? ''),
    queryFn: () => listStaysWithPlacements(patientId as string),
    enabled: !!patientId,
    retry: 1,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
}

/**
 * Read a patient's stays and each stay's movement log.
 *
 * One request per stay after the list, in parallel: the log is a sub-resource
 * and a patient's stays are few. A failure on any leg fails the read: a
 * timeline silently missing a transfer is worse than a visible error, because
 * nothing on screen says it is incomplete.
 */
async function listStaysWithPlacements(patientId: string): Promise<StayWithPlacements[]> {
  const stays = await listStays(patientId);
  return Promise.all(
    stays.map(async (stay) => ({
      ...stay,
      placements: await listStayPlacements(patientId, stay.id),
    })),
  );
}

/**
 * The patient has no admitted stay, so there is nothing to transfer or
 * discharge. A distinct error rather than a silent no-op: the board disables
 * these actions when there is no admitted stay, so reaching here means the
 * cache and the server disagree, and the nurse must be told rather than shown
 * a button that did nothing.
 */
class NoAdmittedStayError extends Error {
  constructor(patientId: string) {
    super(`patient ${patientId} has no admitted stay`);
    this.name = 'NoAdmittedStayError';
  }
}

interface FriendlyEncounterError {
  /** Plain-language, already-localized reason — safe to show the nurse. */
  description: string;
  /**
   * Raw server/network message, set ONLY when the failure isn't one of the
   * codes we recognize below. Unlike `description`, this may be untranslated
   * or (rarely) carry clinical text from a 5xx body — safe to beacon to
   * Cloud Logging via errorToast's `reportMessage`, never to render.
   */
  reportMessage?: string;
}

/**
 * Map a mutation failure to a plain-language, already-localized reason. 4xx
 * bodies carry a structured code (NO_ACTIVE_ENCOUNTER, PATIENT_MOVED, …) the
 * server means for the caller to branch on; anything else (network/5xx) gets
 * the generic fallback. The raw server message never becomes user-facing
 * `description` beyond the codes we explicitly recognize — it's only ever
 * surfaced via `reportMessage`, for the report beacon.
 */
function friendlyEncounterError(err: unknown, fallbackKey: string): FriendlyEncounterError {
  if (err instanceof NoAdmittedStayError) {
    return { description: i18n.t('patientAdmin.errors.noActiveEncounter') };
  }
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    switch (err.code) {
      case 'NO_ACTIVE_ENCOUNTER':
        return { description: i18n.t('patientAdmin.errors.noActiveEncounter') };
      case 'ENCOUNTER_IN_PROGRESS':
        return { description: i18n.t('patientAdmin.errors.encounterInProgress') };
      case 'PATIENT_MOVED':
        return { description: i18n.t('patientAdmin.errors.patientMoved') };
      case 'PATIENT_RE_ADMITTED':
        return { description: i18n.t('patientAdmin.errors.patientReAdmitted') };
      case 'ADMIT_SUPERSEDED':
        return { description: i18n.t('patientAdmin.errors.admitSuperseded') };
      case 'PLACEMENT_REQUIRED':
        return { description: i18n.t('patientAdmin.errors.placementRequired') };
      case 'LOCATION_HAS_CHILDREN':
        return { description: i18n.t('patientAdmin.errors.locationHasChildren') };
      case 'INVALID_CLASS':
        return { description: i18n.t('patientAdmin.errors.invalidClass') };
      case 'INVALID_DISPOSITION':
        return { description: i18n.t('patientAdmin.errors.invalidDisposition') };
      default:
        return { description: i18n.t(fallbackKey), reportMessage: err.message };
    }
  }
  return {
    description: i18n.t(fallbackKey),
    reportMessage: err instanceof Error ? err.message : undefined,
  };
}

interface EncounterListContext {
  previous: StayWithPlacements[] | undefined;
}

function snapshotEncounters(
  qc: ReturnType<typeof useQueryClient>,
  patientId: string,
): StayWithPlacements[] | undefined {
  return qc.getQueryData<StayWithPlacements[]>(encounterKeys.list(patientId));
}

/**
 * A stay mutation moves a patient BETWEEN units, so it invalidates more than
 * the patient's own stay list.
 *
 * Invalidating only the stay list left both wards showing the old truth: the
 * ward the patient left still listed the bed as occupied, the ward they arrived
 * on did not show them, and the two rosters disagreed until someone reloaded.
 * The census reads `patients` and each unit reads `locations/{id}/patients`, so
 * a write that changes where a patient IS has to reach all three.
 *
 * Invalidated by PREFIX, not per unit id: the origin unit is not in scope here
 * (the caller knows the destination, not where the patient came from), and a
 * prefix covers both without the caller having to name either.
 */
function invalidateAfterEncounterMutation(
  qc: ReturnType<typeof useQueryClient>,
  patientId: string,
): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: encounterKeys.list(patientId) }),
    qc.invalidateQueries({ queryKey: patientKeys.all }),
    qc.invalidateQueries({ queryKey: orgKeys.all }),
  ]);
}

/**
 * Shared `onError` (rollback to the pre-mutation snapshot + errorToast) and
 * `onSettled` (invalidate) for the three census mutations below — identical
 * apart from the errorToast context tag and the friendlyEncounterError
 * fallback key. `TVariables` only needs `patientId`, which every one of their
 * mutate() payloads carries.
 */
function encounterMutationDefaults<TVariables extends { patientId: string }>(
  qc: ReturnType<typeof useQueryClient>,
  errorContext: string,
  fallbackKey: string,
) {
  return {
    onError: (err: Error, variables: TVariables, context: EncounterListContext | undefined) => {
      if (context) qc.setQueryData(encounterKeys.list(variables.patientId), context.previous);
      const { description, reportMessage } = friendlyEncounterError(err, fallbackKey);
      errorToast({ context: errorContext, description, reportMessage });
    },
    onSettled: (_data: unknown, _err: Error | null, variables: TVariables) =>
      invalidateAfterEncounterMutation(qc, variables.patientId),
  };
}

interface AdmitPatientVariables {
  patientId: string;
  locationId: string;
  idempotencyKey?: string;
}

/**
 * Admit a patient with NO in-progress stay into a specific unit — reuses
 * `orgApi.admitPatient` (`POST /locations/{location_id}/patients`), which
 * opens the encounter server-side. 409 LOCATION_HAS_CHILDREN if the unit
 * isn't a leaf.
 */
export function useCensusAdmitPatient() {
  const qc = useQueryClient();
  const stayConfig = useStayConfig();

  return useMutation<unknown, Error, AdmitPatientVariables, EncounterListContext>({
    ...encounterMutationDefaults<AdmitPatientVariables>(qc, 'admitPatient', 'patientAdmin.errors.admitFailed'),
    mutationFn: ({ patientId, locationId, idempotencyKey }) =>
      orgApi.admitPatient(locationId, patientId, idempotencyKey),
    onMutate: async ({ patientId, locationId }) => {
      await qc.cancelQueries({ queryKey: encounterKeys.list(patientId) });
      const previous = snapshotEncounters(qc, patientId);
      const now = new Date().toISOString();
      const optimistic: StayWithPlacements = {
        id: `temp-${randomUUID()}`,
        patient: patientId,
        location: locationId,
        class: stayConfig?.default_class ?? '',
        status: 'admitted',
        presence: 'present',
        started_at: now,
        // Empty, not a fabricated admit event: the log is what the server
        // recorded, and the refetch fills it in.
        placements: [],
      };
      qc.setQueryData<StayWithPlacements[]>(encounterKeys.list(patientId), (old) => [
        optimistic,
        ...(old ?? []),
      ]);
      return { previous };
    },
  });
}

interface TransferPatientVariables {
  patientId: string;
  locationId: string;
  idempotencyKey?: string;
}

/**
 * Move the patient's admitted stay to a new unit.
 *
 * The stay is named in the path, so the caller resolves it FIRST: by reading
 * the stays fresh, not from cache. The server no longer has a notion of "the
 * current stay": the retired `/patients/{id}/transfer` picked one itself. A
 * fresh read is the point rather than a cost: the cache can be a moment behind
 * a colleague's discharge, and transferring a stay that closed underneath you
 * is the mistake naming the stay exists to prevent. When there is no admitted
 * stay the mutation fails before the request rather than guessing.
 */
export function useCensusTransferPatient() {
  const qc = useQueryClient();

  return useMutation<
    StayResponse,
    Error,
    TransferPatientVariables,
    EncounterListContext
  >({
    ...encounterMutationDefaults<TransferPatientVariables>(
      qc,
      'transferPatient',
      'patientAdmin.errors.transferFailed',
    ),
    mutationFn: async ({ patientId, locationId, idempotencyKey }) => {
      const stay = admittedStay(await listStays(patientId));
      if (!stay) throw new NoAdmittedStayError(patientId);
      return transferStay(patientId, stay.id, locationId, idempotencyKey);
    },
    onMutate: async ({ patientId, locationId }) => {
      await qc.cancelQueries({ queryKey: encounterKeys.list(patientId) });
      const previous = snapshotEncounters(qc, patientId);
      qc.setQueryData<StayWithPlacements[]>(encounterKeys.list(patientId), (old) =>
        // Keyed on the STATUS, not on index 0. The mutation transfers the
        // admitted stay wherever it sits, and a readmitted patient's list can
        // carry a planned or discharged stay ahead of it: decorating row 0
        // would show the move on a stay nobody touched until the invalidate
        // corrected it. The server admits at most one stay at a time, so this
        // matches exactly one row.
        (old ?? []).map((e) =>
          e.status === 'admitted'
            ? {
                ...e,
                // Only the field the user is changing. The movement log is the
                // SERVER's record of what happened, and a fabricated row in it
                // carries an invented seq and an empty actor: a synthetic
                // event a nurse reads as real, and one that survives on screen
                // if the refetch behind it fails.
                location: locationId,
              }
            : e,
        ),
      );
      return { previous };
    },
  });
}

interface DischargePatientVariables {
  patientId: string;
  disposition: string;
  idempotencyKey?: string;
}

/** Close the patient's in-progress stay with a disposition. */
export function useCensusDischargePatient() {
  const qc = useQueryClient();

  return useMutation<StayResponse, Error, DischargePatientVariables, EncounterListContext>({
    ...encounterMutationDefaults<DischargePatientVariables>(
      qc,
      'dischargePatient',
      'patientAdmin.errors.dischargeFailed',
    ),
    mutationFn: async ({ patientId, disposition, idempotencyKey }) => {
      const stay = admittedStay(await listStays(patientId));
      if (!stay) throw new NoAdmittedStayError(patientId);
      return dischargeStay(patientId, stay.id, { disposition }, idempotencyKey);
    },
    onMutate: async ({ patientId, disposition }) => {
      await qc.cancelQueries({ queryKey: encounterKeys.list(patientId) });
      const previous = snapshotEncounters(qc, patientId);
      const now = new Date().toISOString();
      qc.setQueryData<StayWithPlacements[]>(encounterKeys.list(patientId), (old) =>
        // Keyed on the status for the same reason as the transfer above.
        (old ?? []).map((e) =>
          e.status === 'admitted'
            ? {
                ...e,
                // The fields the user is changing; the movement log is the
                // server's record. See the transfer above.
                status: 'discharged' as const,
                location: null,
                ended_at: now,
                disposition,
              }
            : e,
        ),
      );
      return { previous };
    },
  });
}
